import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import type { DateRange } from './DateRangePicker';

interface PeriodMetrics {
  spend: number;
  conversions: number;
  cpa: number;
  ctr: number;
  cvr: number;
  roas: number;
  clicks: number;
  impressions: number;
  conv_value?: number;
  impression_share?: number | null;
  budget_lost_is?: number | null;
  rank_lost_is?: number | null;
  all_conversions?: number;
  all_conv_value?: number;
}

interface Metrics extends PeriodMetrics {
  prev?: PeriodMetrics;
  d30?: PeriodMetrics;
  d90?: PeriodMetrics;
}

interface MetricStripProps {
  accountId?: string;
  campaignType?: string;
  dateRange?: DateRange;
}

type TrendInfo = { pct: string; direction: 'up' | 'down' | 'flat' };

function trendDelta(current: number, previous: number | undefined | null): TrendInfo {
  if (previous === undefined || previous === null || previous === 0) return { pct: '', direction: 'flat' };
  const delta = ((current - previous) / previous) * 100;
  if (Math.abs(delta) < 0.5) return { pct: '0%', direction: 'flat' };
  return {
    pct: `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`,
    direction: delta > 0 ? 'up' : 'down',
  };
}

function formatCurrency(val: number) {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
  return `$${val.toFixed(2)}`;
}

function formatNumber(val: number) {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
  return val.toLocaleString();
}

function Sparkline({ points, invertColor }: { points: (number | null)[]; invertColor?: boolean }) {
  const valid = points.filter((p): p is number => p !== null && p !== undefined);
  if (valid.length < 2) return null;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = max - min || 1;
  const w = 48;
  const h = 16;
  const pad = 1;
  const coords = valid.map((v, i) => {
    const x = pad + (i / (valid.length - 1)) * (w - 2 * pad);
    const y = h - pad - ((v - min) / range) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const trending = valid[valid.length - 1] >= valid[0];
  const isPositive = invertColor ? !trending : trending;
  const color = isPositive ? 'var(--color-positive)' : 'var(--color-negative)';
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="kpi-sparkline">
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrendArrow({ direction, invertColor }: { direction: 'up' | 'down' | 'flat'; invertColor: boolean }) {
  if (direction === 'flat') return null;
  const isPositive = invertColor ? direction === 'down' : direction === 'up';
  return (
    <svg viewBox="0 0 10 6" width="8" height="5" className={`kpi-trend-arrow ${isPositive ? 'positive' : 'negative'}`}>
      {direction === 'up' ? (
        <polyline points="0,6 5,0 10,6" fill="none" stroke="currentColor" strokeWidth="1.5" />
      ) : (
        <polyline points="0,0 5,6 10,0" fill="none" stroke="currentColor" strokeWidth="1.5" />
      )}
    </svg>
  );
}

interface KPIItemProps {
  label: string;
  value: string;
  trend: TrendInfo;
  invertColor?: boolean;
  sub?: string | null;
  sparkPoints?: (number | null)[];
  d30Value?: string | null;
  d90Value?: string | null;
}

function KPIItem({ label, value, trend, invertColor = false, sub, sparkPoints, d30Value, d90Value }: KPIItemProps) {
  const trendClass = () => {
    if (trend.direction === 'flat') return 'kpi-trend flat';
    const isPositive = invertColor ? trend.direction === 'down' : trend.direction === 'up';
    return `kpi-trend ${isPositive ? 'positive' : 'negative'}`;
  };

  return (
    <div className="kpi-item">
      <div className="kpi-item-header">
        <span className="kpi-label">{label}</span>
        {sparkPoints && sparkPoints.length >= 2 && (
          <Sparkline points={sparkPoints} invertColor={invertColor} />
        )}
      </div>
      <div className="kpi-item-main">
        <span className="kpi-value">{value}</span>
        {trend.pct && (
          <span className={trendClass()}>
            <TrendArrow direction={trend.direction} invertColor={invertColor} />
            {trend.pct}
          </span>
        )}
      </div>
      {(d30Value || d90Value) && (
        <div className="kpi-periods">
          {d30Value && <span className="kpi-period">30d: {d30Value}</span>}
          {d90Value && <span className="kpi-period">90d: {d90Value}</span>}
        </div>
      )}
      {sub && <span className="kpi-sub">{sub}</span>}
    </div>
  );
}

function getMetricVal(m: PeriodMetrics | undefined, key: keyof PeriodMetrics): number | null {
  if (!m) return null;
  const v = m[key];
  if (v === null || v === undefined) return null;
  return typeof v === 'number' ? v : null;
}

export default function MetricStrip({ accountId, campaignType, dateRange }: MetricStripProps) {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);

  const isAgent = user?.plan === 'agent' || user?.email?.toLowerCase().endsWith('@zimamedia.com');

  const loadMetrics = useCallback(async () => {
    if (!isAgent) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (accountId) params.set('account_id', accountId);
      if (campaignType) params.set('campaign_type', campaignType);
      if (dateRange) {
        params.set('date_start', dateRange.startDate);
        params.set('date_end', dateRange.endDate);
        params.set('date_preset', dateRange.preset);
      }
      const res = await fetch(`/api/chat/metrics?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setMetrics(data.metrics);
        setStatusMessage(data.message || null);
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [isAgent, accountId, campaignType, dateRange?.startDate, dateRange?.endDate, dateRange?.preset]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  if (!isAgent) return null;

  const prev = metrics?.prev;
  const d30 = metrics?.d30;
  const d90 = metrics?.d90;

  function sparkFor(key: keyof PeriodMetrics): (number | null)[] {
    if (!metrics) return [];
    return [getMetricVal(d90, key), getMetricVal(d30, key), getMetricVal(metrics, key)];
  }

  return (
    <div className={`kpi-cards${collapsed ? ' collapsed' : ''}`}>
      <button
        className="kpi-toggle"
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? 'Show KPI cards' : 'Hide KPI cards'}
      >
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
          {collapsed ? (
            <polyline points="6 9 12 15 18 9" />
          ) : (
            <polyline points="18 15 12 9 6 15" />
          )}
        </svg>
      </button>

      {!collapsed && (
        <>
          {loading && !metrics && (
            <div className="kpi-loading">Loading metrics...</div>
          )}
          {!loading && !metrics && (
            <div className="kpi-loading">{statusMessage || 'No metrics available'}</div>
          )}
          {metrics && (
            <div className="kpi-sections">
              <div className="kpi-section">
                <div className="kpi-section-label">Spend & Visibility</div>
                <div className="kpi-section-items">
                  <KPIItem
                    label="Spend"
                    value={formatCurrency(metrics.spend)}
                    trend={trendDelta(metrics.spend, prev?.spend)}
                    sparkPoints={sparkFor('spend')}
                    d30Value={d30 ? formatCurrency(d30.spend) : null}
                    d90Value={d90 ? formatCurrency(d90.spend) : null}
                  />
                  <KPIItem
                    label="Impression Share"
                    value={metrics.impression_share != null ? `${metrics.impression_share}%` : '\u2014'}
                    trend={metrics.impression_share != null ? trendDelta(metrics.impression_share, prev?.impression_share) : { pct: '', direction: 'flat' as const }}
                    sparkPoints={sparkFor('impression_share')}
                    d30Value={d30?.impression_share != null ? `${d30.impression_share}%` : null}
                    d90Value={d90?.impression_share != null ? `${d90.impression_share}%` : null}
                    sub={
                      metrics.budget_lost_is != null || metrics.rank_lost_is != null
                        ? `Lost: ${metrics.budget_lost_is != null ? `${metrics.budget_lost_is}% budget` : ''}${metrics.budget_lost_is != null && metrics.rank_lost_is != null ? ' \u00B7 ' : ''}${metrics.rank_lost_is != null ? `${metrics.rank_lost_is}% rank` : ''}`
                        : null
                    }
                  />
                </div>
              </div>

              <div className="kpi-section">
                <div className="kpi-section-label">Engagement</div>
                <div className="kpi-section-items">
                  <KPIItem
                    label="CTR"
                    value={`${metrics.ctr.toFixed(2)}%`}
                    trend={trendDelta(metrics.ctr, prev?.ctr)}
                    sparkPoints={sparkFor('ctr')}
                    d30Value={d30 ? `${d30.ctr.toFixed(2)}%` : null}
                    d90Value={d90 ? `${d90.ctr.toFixed(2)}%` : null}
                    sub={`${formatNumber(metrics.impressions)} impressions`}
                  />
                  <KPIItem
                    label="Conv. Rate"
                    value={`${metrics.cvr.toFixed(2)}%`}
                    trend={trendDelta(metrics.cvr, prev?.cvr)}
                    sparkPoints={sparkFor('cvr')}
                    d30Value={d30 ? `${d30.cvr.toFixed(2)}%` : null}
                    d90Value={d90 ? `${d90.cvr.toFixed(2)}%` : null}
                    sub={`${formatNumber(metrics.clicks)} clicks`}
                  />
                </div>
              </div>

              <div className="kpi-section">
                <div className="kpi-section-label">Results</div>
                <div className="kpi-section-items">
                  <KPIItem
                    label="CPA"
                    value={formatCurrency(metrics.cpa)}
                    trend={trendDelta(metrics.cpa, prev?.cpa)}
                    invertColor={true}
                    sparkPoints={sparkFor('cpa')}
                    d30Value={d30 ? formatCurrency(d30.cpa) : null}
                    d90Value={d90 ? formatCurrency(d90.cpa) : null}
                  />
                  <KPIItem
                    label="Conversions"
                    value={formatNumber(metrics.conversions)}
                    trend={trendDelta(metrics.conversions, prev?.conversions)}
                    sparkPoints={sparkFor('conversions')}
                    d30Value={d30 ? formatNumber(d30.conversions) : null}
                    d90Value={d90 ? formatNumber(d90.conversions) : null}
                    sub={metrics.all_conversions && metrics.all_conversions > metrics.conversions
                      ? `${formatNumber(metrics.all_conversions)} all conv.`
                      : undefined}
                  />
                  <KPIItem
                    label="Conv. Value"
                    value={formatCurrency(metrics.conv_value ?? 0)}
                    trend={trendDelta(metrics.conv_value ?? 0, prev?.conv_value ?? undefined)}
                    sparkPoints={[getMetricVal(d90, 'conv_value'), getMetricVal(d30, 'conv_value'), metrics.conv_value ?? null]}
                    d30Value={d30?.conv_value != null ? formatCurrency(d30.conv_value) : null}
                    d90Value={d90?.conv_value != null ? formatCurrency(d90.conv_value) : null}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
